import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const ExamSelector = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        const loadExams = async () => {
            try {
                // Pass user.id if logged in, otherwise default (null or handled by API default if strict param missing)
                const userId = user ? user.id : null;
                const data = await api.getExamCatalog('pstar', userId);
                setExams(data);
            } catch (error) {
                console.error("Failed to load exam catalog", error);
            } finally {
                setLoading(false);
            }
        };
        loadExams();
    }, []);

    if (loading) {
        return <div className="p-8 text-center text-gray-500">{language === 'fr' ? 'Chargement du catalogue...' : 'Loading catalog...'}</div>;
    }

    const categories = [
        { id: 'all', label: { fr: 'Tout', en: 'All' } },
        { id: 'meteo', label: { fr: 'Météorologie', en: 'Meteorology' } },
        { id: 'law', label: { fr: 'Droit', en: 'Law' } },
        { id: 'nav', label: { fr: 'Navigation', en: 'Navigation' } },
        { id: 'gen', label: { fr: 'Général', en: 'General' } },
    ];

    const filteredExams = selectedCategory === 'all'
        ? exams
        : exams.filter(exam => exam.category === selectedCategory);

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy': return 'bg-green-100 text-green-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'hard': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-lg shadow-sm">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                        {language === 'fr' ? 'Sélectionner un Examen' : 'Select an Exam'}
                    </h2>
                    <p className="text-gray-500 mt-1">
                        {language === 'fr' ? 'Choisissez un module pour commencer l\'entraînement.' : 'Choose a module to start training.'}
                    </p>
                </div>
                {/* Category Filters */}
                <div className="mt-4 md:mt-0 flex space-x-2 overflow-x-auto no-scrollbar pb-2 md:pb-0">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap
                                ${selectedCategory === cat.id
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {language === 'fr' ? cat.label.fr : cat.label.en}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredExams.map(exam => (
                    <div key={exam.id} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
                        <div className="h-32 bg-gray-200 relative">
                            {/* Placeholder for exam image since we don't have real assets yet */}
                            <div className={`absolute inset-0 flex items-center justify-center text-4xl font-bold text-white opacity-40 bg-gradient-to-br from-blue-400 to-indigo-600`}>
                                {exam.category.toUpperCase().substring(0, 2)}
                            </div>
                            <div className="absolute top-2 right-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wide ${getDifficultyColor(exam.difficulty)}`}>
                                    {exam.difficulty}
                                </span>
                            </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {language === 'fr' ? exam.title.fr : exam.title.en}
                            </h3>
                            <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                                <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {exam.questionsCount} Q
                                </span>
                                <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {exam.duration} min
                                </span>
                            </div>

                            {/* Stats: Progression & Score */}
                            <div className="mb-4 space-y-3">
                                <div>
                                    <div className="flex justify-between text-xs font-semibold uppercase text-gray-500 mb-1">
                                        <span>{language === 'fr' ? 'Progression' : 'Progression'}</span>
                                        <span className="text-gray-900">{exam.progression || 0}%</span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-2">
                                        <div
                                            className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                            style={{ width: `${exam.progression || 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-xs font-semibold uppercase text-gray-500 mb-1">
                                        <span>Score</span>
                                        <span className={exam.bestScore !== null ? "text-gray-900" : "text-gray-400"}>
                                            {exam.bestScore !== null ? `${exam.bestScore}%` : '-'}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-500 ${exam.bestScore !== null ? 'bg-orange-500' : 'bg-transparent'}`}
                                            style={{ width: `${exam.bestScore || 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            <button className="mt-auto w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-transform transform active:scale-95">
                                {language === 'fr' ? 'Commencer' : 'Start Now'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ExamSelector;
